import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { isMobileBrowser } from '../../../base/environment/utils';
import { isLocalParticipantModerator } from '../../../base/participants/functions';
import { equals } from '../../../base/redux/functions';
import useContextMenu from '../../../base/ui/hooks/useContextMenu.web';
import { ParticipantContainer } from '../../../participants-pane/components/styled';
import { findStyledAncestor } from '../../../participants-pane/functions';
import {
    getBreakoutRooms,
    getBreakoutRoomsConfig,
    getCurrentRoomId,
    isAutoAssignParticipantsVisible,
    isInBreakoutRoom
} from '../../functions';

import { AutoAssignButton } from './AutoAssignButton';
import { CollapsibleRoom } from './CollapsibleRoom';
import JoinActionButton from './JoinQuickActionButton';
import { LeaveButton } from './LeaveButton';
import RoomActionEllipsis from './RoomActionEllipsis';
import { RoomContextMenu } from './RoomContextMenu';
import { RoomParticipantContextMenu } from './RoomParticipantContextMenu';

const useStyles = makeStyles()(() => {
    return {
        topMargin: {
            marginTop: 16
        },
        autoAssignButton: {
            marginTop: 16,
            backgroundColor: '#4b6790',
            color: '#fff'
        }
    };
});

const initialState = Object.freeze(Object.create(null));

export const RoomList = ({ searchString }) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const currentRoomId = useSelector(getCurrentRoomId);
    const rooms = Object.values(useSelector(getBreakoutRooms, equals))
                    .filter(room => room.id !== currentRoomId)
                    .sort((p1, p2) => (p1?.name || '').localeCompare(p2?.name || ''));
    const inBreakoutRoom = useSelector(isInBreakoutRoom);
    const isLocalModerator = useSelector(isLocalParticipantModerator);
    const showAutoAssign = useSelector(isAutoAssignParticipantsVisible);
    const { hideJoinRoomButton } = useSelector(getBreakoutRoomsConfig);
    const [ lowerMenu, raiseMenu, toggleMenu, menuEnter, menuLeave, raiseContext ] = useContextMenu();
    const persistentAutoAssign = useSelector(state => state['features/breakout-rooms'].persistentAutoAssign || false);

    // const [ lowerParticipantMenu, raiseParticipantMenu, toggleParticipantMenu,
    //     participantMenuEnter, participantMenuLeave, raiseParticipantContext ] = useContextMenu();
    const onRaiseMenu = useCallback(room => target => raiseMenu(room, target), [ raiseMenu ]);
    const isMouseOverMenu = useRef(false);

    const [ raiseParticipantContext, setPaiseParticipantContext ] = useState(initialState);

    const lowerParticipantMenu = useCallback(() => {
        /**
         * We are tracking mouse movement over the active participant item and
         * the context menu. Due to the order of enter/leave events, we need to
         * defer checking if the mouse is over the context menu with
         * queueMicrotask
         */
        window.queueMicrotask(() => {
            if (isMouseOverMenu.current) {
                return;
            }

            if (raiseContext !== initialState) {
                setPaiseParticipantContext(initialState);
            }
        });
    }, [ raiseParticipantContext ]);

    const raiseParticipantMenu = useCallback((participant, target) => {
        setPaiseParticipantContext({
            participant,
            offsetTarget: findStyledAncestor(target, ParticipantContainer)
        });
    }, [ raiseParticipantContext ]);

    const toggleParticipantMenu = useCallback(participant => e => {
        const { participant: raisedParticipant } = raiseParticipantContext;

        if (raisedParticipant?.jid === participant.jid) {
            lowerParticipantMenu();
        } else {
            raiseParticipantMenu(participant, e.target);
        }
    }, [ raiseParticipantContext ]);

    const participantMenuEnter = useCallback(() => {
        isMouseOverMenu.current = true;
    }, []);

    const participantMenuLeave = useCallback(() => {
        isMouseOverMenu.current = false;
        lowerParticipantMenu();
    }, [ lowerParticipantMenu ]);

    // close the menu when the room vanishes
    useEffect(() => {
        if (raiseContext.entity && !rooms.some(r => r.id === raiseContext.entity?.id)) {
            lowerMenu();
        }
    }, [ raiseContext, rooms, lowerMenu ]);

    return (
        <>
            {inBreakoutRoom && <LeaveButton className = { classes.topMargin } />}
            {showAutoAssign && !persistentAutoAssign && <AutoAssignButton className = { classes.autoAssignButton } />}
            {!showAutoAssign && persistentAutoAssign && <AutoAssignButton className = { classes.autoAssignButton } />}
            <div
                aria-label = { t('breakoutRooms.breakoutList', 'breakout list') }
                className = { classes.topMargin }
                id = 'breakout-rooms-list'
                role = 'list'>
                {rooms.map(room => (
                    <React.Fragment key = { room.id }>
                        <CollapsibleRoom
                            isHighlighted = { true }
                            onRaiseMenu = { onRaiseMenu(room) }
                            participantContextEntity = { raiseParticipantContext.participant }
                            raiseParticipantContextMenu = { raiseParticipantMenu }
                            room = { room }
                            searchString = { searchString }
                            toggleParticipantMenu = { toggleParticipantMenu }>
                            {!isMobileBrowser() && <>
                                {!hideJoinRoomButton && <JoinActionButton room = { room } />}
                                {isLocalModerator && !room.isMainRoom
                                    && <RoomActionEllipsis onClick = { toggleMenu(room) } />}
                            </>}
                        </CollapsibleRoom>
                    </React.Fragment>
                ))}
            </div>
            <RoomContextMenu
                onEnter = { menuEnter }
                onLeave = { menuLeave }
                onSelect = { lowerMenu }
                { ...raiseContext } />
            <RoomParticipantContextMenu
                onEnter = { participantMenuEnter }
                onLeave = { participantMenuLeave }
                onSelect = { lowerParticipantMenu }
                { ...raiseParticipantContext } />
        </>
    );
};
